import {
  describeApiError,
  type RoomFeature,
  type Template,
  type TemplateFieldType,
  type TemplateKind,
  type TemplatePurpose,
} from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { ChevronDown, ChevronUp, Plus, StickyNote, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { FEATURES } from '../features';
import { PURPOSES, purposesFor } from '../purposes';
import { useCreateTemplate, useDeleteTemplate, useUpdateTemplate } from '../queries';
import {
  contentLine,
  fieldsLegend,
  PLAYERS_FIELDS,
  PLAYERS_PURPOSE,
  purposeChoices as offeredPurposes,
  templateControls,
} from '../templates/playersTemplate';

const controlClass =
  'rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-950 outline-none focus:border-felt-500';

const inputClass = `w-full ${controlClass}`;

/**
 * Row controls carry no width of their own: `w-full` here would collide with the sizing each
 * one needs as a flex child, and which utility wins is decided by stylesheet order rather than
 * class order — which is how the select ended up swallowing the whole row.
 */
const rowInputClass = `min-w-0 flex-1 ${controlClass}`;

/**
 * Held separately from the wire type so the form always has concrete strings to bind to,
 * whatever the contract does with nullability.
 */
interface FieldDraft {
  key: string;
  label: string;
  required: boolean;
  dataType: TemplateFieldType;
  notes: string;
  /**
   * Whether the notes editor is open. Carried on the draft rather than in a set of indices so
   * it travels with its row: rows reorder and delete by position, and index-keyed UI state
   * would leave the wrong row open the moment one moves.
   */
  notesOpen: boolean;
}

const emptyField = (): FieldDraft => ({
  key: '',
  label: '',
  required: false,
  dataType: 'string',
  notes: '',
  notesOpen: false,
});

/**
 * What the dialog was opened to do.
 *
 * `clone` is a create that starts from an existing definition, not a third kind of save — the
 * server has no notion of a copy, and a cloned template is simply a new one that happened to be
 * typed for you. Modelled as its own mode rather than "edit with the id removed" so the delete
 * section and the fixed-kind rule can key off `edit` alone.
 */
export type TemplateIntent =
  | { mode: 'create' }
  | { mode: 'clone'; source: Template; suggestedName: string }
  | { mode: 'edit'; template: Template };

export function TemplateModal({
  intent,
  playersTemplateExists,
  onClose,
}: {
  intent: TemplateIntent;
  /** Whether the catalogue already holds the players template, which decides whether a new one is offered (PI68). */
  playersTemplateExists: boolean;
  onClose: () => void;
}) {
  const template = intent.mode === 'edit' ? intent.template : null;
  // Derived from `template`, not from `intent.mode`, even though the two say the same thing:
  // as an aliased `!== null` check this narrows `template` for TypeScript everywhere `editing`
  // gates a block, which the delete section and the fixed-kind notice both rely on.
  const editing = template !== null;

  // The definition the form starts from: the template being edited, the one being copied, or
  // nothing at all.
  const seed = intent.mode === 'clone' ? intent.source : template;

  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();

  const [name, setName] = useState(
    intent.mode === 'clone' ? intent.suggestedName : (seed?.name ?? ''),
  );
  // Only meaningful while creating: the server refuses to change it afterwards, because a
  // template's kind decides what every room's stored content for it means. A clone is a
  // creation, so its kind starts from the source but stays selectable.
  const [kind, setKind] = useState<TemplateKind>((seed?.kind as TemplateKind) ?? 'html');
  // Editable after creation, unlike the kind: a room's stored content means the same thing
  // whichever job the template is put to, so changing this only changes which lists it appears in.
  // A copy of the players template starts as General: there is only one players template (PI68).
  const offered = (forKind: TemplateKind) =>
    offeredPurposes(
      purposesFor(forKind).map((p) => p.value),
      { mode: intent.mode, storedPurpose: (template?.purpose as TemplatePurpose) ?? null, playersTemplateExists },
    );
  const [purpose, setPurpose] = useState<TemplatePurpose>(() => {
    const seeded = (seed?.purpose as TemplatePurpose) ?? 'general';
    return offered((seed?.kind as TemplateKind) ?? 'html').includes(seeded) ? seeded : 'general';
  });
  const [features, setFeatures] = useState<RoomFeature[]>((seed?.features as RoomFeature[]) ?? []);
  const [fields, setFields] = useState<FieldDraft[]>(
    seed?.fields.map((f) => ({
      key: f.key,
      label: f.label,
      required: f.required,
      dataType: f.dataType as TemplateFieldType,
      notes: f.notes ?? '',
      // Existing notes open closed too — the summary line says they are there, which is the
      // point of collapsing them at all.
      notesOpen: false,
    })) ?? [],
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Deleting takes every room's content for the template with it, so the confirmation asks for
  // the name rather than a second click: a click can be muscle memory, typing the name cannot.
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const setField = (index: number, patch: Partial<FieldDraft>) =>
    setFields((fs) => fs.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  const toggleFeature = (value: RoomFeature) =>
    setFeatures((fs) => (fs.includes(value) ? fs.filter((f) => f !== value) : [...fs, value]));

  const move = (index: number, by: number) =>
    setFields((fs) => {
      const to = index + by;
      if (to < 0 || to >= fs.length) return fs;
      const next = [...fs];
      [next[index], next[to]] = [next[to]!, next[index]!];
      return next;
    });

  const isCsv = kind === 'csv';

  /**
   * Changing the kind can strand the purpose — importing tournaments reads columns, so it is
   * CSV-only. Falling back to "General" beats leaving a selection the server would reject, and
   * beats a select displaying a value no longer on its own menu.
   */
  const chooseKind = (next: TemplateKind) => {
    setKind(next);
    if (!offered(next).includes(purpose)) setPurpose('general');
  };

  /**
   * Choosing the players purpose on a new template starts it from the seven fields with no feature gate — the only
   * shape the server accepts for it (PI66) — rather than letting a form be built that cannot be saved.
   */
  const choosePurpose = (next: TemplatePurpose) => {
    setPurpose(next);
    if (next === PLAYERS_PURPOSE) {
      setFeatures([]);
      setFields(PLAYERS_FIELDS.map((f) => ({ ...emptyField(), ...f })));
    }
  };

  const controls = templateControls(intent.mode, purpose);
  const legend = fieldsLegend(controls.fieldSetLocked);
  const purposeChoices = PURPOSES.filter((p) => offered(kind).includes(p.value));
  const purposeNote = PURPOSES.find((p) => p.value === purpose)?.description;

  const trimmedKeys = fields.map((f) => f.key.trim().toLowerCase()).filter((k) => k.length > 0);
  const duplicateKey = trimmedKeys.length !== new Set(trimmedKeys).size;

  // Mirrors what the server enforces, so the ordinary case never round-trips to find out.
  const valid =
    name.trim().length > 0 &&
    (!isCsv ||
      (fields.length > 0 &&
        fields.every((f) => f.key.trim().length > 0 && f.label.trim().length > 0) &&
        !duplicateKey));

  // Matched against the saved name, not the draft in the form: what gets deleted is the stored
  // template, so an unsaved rename must not change the answer. Trimmed, because a stray space is
  // not a different answer — but otherwise exact, including case. Reading the name closely enough
  // to reproduce it is the whole point of the gate.
  const deleteArmed = template !== null && deleteConfirmation.trim() === template.name;

  const closeDeleteConfirmation = () => {
    setConfirmDelete(false);
    setDeleteConfirmation('');
  };

  const saving = createTemplate.isPending || updateTemplate.isPending;
  const error =
    describeApiError(createTemplate.error) ??
    describeApiError(updateTemplate.error) ??
    describeApiError(deleteTemplate.error);

  const submit = () => {
    if (!valid) return;
    // Array order is the catalogue order; the server assigns positions from it.
    const payloadFields = isCsv
      ? fields.map((f) => ({
          key: f.key.trim(),
          label: f.label.trim(),
          required: f.required,
          dataType: f.dataType,
          notes: f.notes.trim() || null,
        }))
      : null;

    if (template) {
      updateTemplate.mutate(
        { id: template.id, body: { name: name.trim(), purpose, features, fields: payloadFields } },
        { onSuccess: onClose },
      );
    } else {
      createTemplate.mutate(
        { name: name.trim(), kind, purpose, features, fields: payloadFields },
        { onSuccess: onClose },
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-6"
      role="dialog"
      aria-modal="true"
    >
      {/* Wider than the other dialogs here: a field row carries four controls plus reorder
          buttons, and cramming them into max-w-2xl is what makes the inputs unusable. */}
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl">
        <header className="border-b border-ink-800/10 p-6">
          <h3 className="font-display text-lg font-semibold text-ink-950">
            {editing
              ? 'Edit template'
              : intent.mode === 'clone'
                ? 'Duplicate template'
                : 'New template'}
          </h3>
          <p className="mt-0.5 text-xs text-ink-800/60">
            {intent.mode === 'clone' ? (
              <>
                A copy of <span className="font-medium text-ink-950">{intent.source.name}</span>,
                not a link to it — later edits to one leave the other alone. Nothing is created
                until you save, and no room&apos;s content comes across: every room fills the copy
                in from scratch.
              </>
            ) : (
              contentLine(purpose)
            )}
          </p>
        </header>

        <div className="flex-1 space-y-4 overflow-auto p-6">
          <label className="block">
            <span className="mb-1 block text-xs text-ink-800/60">Template name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Tournament results export"
              className={inputClass}
            />
          </label>

          <div>
            <span className="mb-1 block text-xs text-ink-800/60">Type</span>
            {editing ? (
              <p className="rounded-lg bg-ink-800/5 px-3 py-2 text-sm text-ink-800/70">
                {template.kind === 'csv' ? 'CSV' : 'HTML'} — a template&apos;s type is fixed once
                it exists, because it decides what every room&apos;s content for it means.
              </p>
            ) : (
              <select
                value={kind}
                onChange={(e) => chooseKind(e.target.value as TemplateKind)}
                className={inputClass}
              >
                <option value="html">HTML — rooms supply markup (Handlebars)</option>
                <option value="csv">CSV — rooms choose and order the fields below</option>
              </select>
            )}
          </div>

          {/* What the platform does with it, as opposed to who sees it. Sits next to the type
              because the two constrain each other: importing reads columns, so it needs CSV. */}
          <div>
            <span className="mb-1 block text-xs text-ink-800/60">Used for</span>
            {controls.purposeFixed ? (
              <p className="rounded-lg bg-ink-800/5 px-3 py-2 text-sm text-ink-800/70">
                Import players — fixed: this is the template the player import reads.
              </p>
            ) : (
              <select
                value={purpose}
                onChange={(e) => choosePurpose(e.target.value as TemplatePurpose)}
                className={inputClass}
              >
                {purposeChoices.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
            {purposeNote ? <p className="mt-1 text-xs text-ink-800/50">{purposeNote}</p> : null}
            {!isCsv && purposeChoices.length < PURPOSES.length ? (
              <p className="mt-1 text-xs text-ink-800/40">
                Purposes that read columns need a CSV template.
              </p>
            ) : null}
          </div>

          <fieldset className="min-w-0 border-t border-ink-800/10 pt-4">
            <legend className="text-xs text-ink-800/60">Available to rooms running</legend>
            <p className="mt-1 mb-2 text-xs text-ink-800/50">
              {controls.featuresLocked
                ? 'Every room — the players template is not limited to features, so every room can fill it in.'
                : features.length === 0
                  ? 'Nothing selected — every room sees this template.'
                  : 'A room sees this template if it runs any one of these. Rooms running none of them never see it.'}
            </p>
            <div className={controls.featuresLocked ? 'hidden' : 'space-y-2'}>
              {FEATURES.map(({ value, label, icon: Icon }) => (
                <label key={value} className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={features.includes(value)}
                    onChange={() => toggleFeature(value)}
                    className="rounded"
                  />
                  <Icon className="size-4 text-ink-800/60" />
                  <span className="text-sm text-ink-800">{label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {isCsv ? (
            /* min-w-0 is doing real work: a fieldset's UA default is
               min-inline-size: min-content, which overrides flex shrinking and pushes the row
               into horizontal overflow as soon as a vertical scrollbar narrows the body. */
            <fieldset className="min-w-0 border-t border-ink-800/10 pt-4">
              <legend className={legend.visuallyHidden ? 'sr-only' : 'text-xs text-ink-800/60'}>
                {legend.text}
              </legend>

              {fields.length > 0 ? (
                // The inputs are narrow once four controls share a row, so the columns are
                // named once here rather than relying on placeholders that vanish on typing.
                <div className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-wide text-ink-800/45">
                  <span className="min-w-0 flex-1">Key</span>
                  <span className="min-w-0 flex-1">Header label</span>
                  <span className="w-28 shrink-0">Data type</span>
                  <span className="w-20 shrink-0">Required</span>
                  <span className="w-[84px] shrink-0" />
                </div>
              ) : null}

              <div className="mt-1.5 space-y-2">
                {fields.map((field, index) => (
                  // Index as key: rows are positional and reorder in place, so there is no
                  // stable id to use until the server has one.
                  <div
                    key={index}
                    className="space-y-1.5 border-b border-ink-800/5 pb-2 last:border-b-0 last:pb-0"
                  >
                    <div className="flex items-center gap-2">
                      {controls.fieldSetLocked ? (
                        <span className="min-w-0 flex-1 truncate px-3 py-2 font-mono text-sm text-ink-800/70">
                          {field.key}
                        </span>
                      ) : (
                        <input
                          value={field.key}
                          onChange={(e) => setField(index, { key: e.target.value })}
                          placeholder="player_name"
                          aria-label={`Field ${index + 1} key`}
                          className={`${rowInputClass} font-mono`}
                        />
                      )}
                      <input
                        value={field.label}
                        onChange={(e) => setField(index, { label: e.target.value })}
                        placeholder="Player Name"
                        aria-label={`Field ${index + 1} label`}
                        className={rowInputClass}
                      />
                      {controls.fieldSetLocked ? (
                        <>
                          <span className="w-28 shrink-0 px-3 text-sm text-ink-800/60">String</span>
                          <span className="w-20 shrink-0 text-center text-xs text-ink-800/60">
                            {field.required ? 'Required' : 'Optional'}
                          </span>
                          <span className="w-[84px] shrink-0" />
                        </>
                      ) : (
                        <>
                          <select
                            value={field.dataType}
                            onChange={(e) =>
                              setField(index, { dataType: e.target.value as TemplateFieldType })
                            }
                            aria-label={`Field ${index + 1} data type`}
                            className={`w-28 shrink-0 ${controlClass}`}
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="time">Time</option>
                            <option value="boolean">Boolean</option>
                          </select>
                          <label className="flex w-20 shrink-0 cursor-pointer items-center justify-center">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => setField(index, { required: e.target.checked })}
                              aria-label={`Field ${index + 1} required`}
                              className="rounded"
                            />
                          </label>
                          <div className="flex w-[84px] shrink-0">
                            <button
                              type="button"
                              onClick={() => move(index, -1)}
                              disabled={index === 0}
                              aria-label={`Move field ${index + 1} up`}
                              className="p-1 text-ink-800/40 hover:text-ink-950 disabled:opacity-25"
                            >
                              <ChevronUp className="size-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => move(index, 1)}
                              disabled={index === fields.length - 1}
                              aria-label={`Move field ${index + 1} down`}
                              className="p-1 text-ink-800/40 hover:text-ink-950 disabled:opacity-25"
                            >
                              <ChevronDown className="size-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setFields((fs) => fs.filter((_, i) => i !== index))}
                              aria-label={`Remove field ${index + 1}`}
                              className="p-1 text-ink-800/40 hover:text-chip-500"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Notes are prose and need a line of their own, but most fields never get
                        one — left open by default they cost a full input per row and bury the
                        table. Collapsed, the row still says whether a note exists and what it
                        says, so nothing is hidden, only folded. */}
                    <div className="flex min-w-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setField(index, { notesOpen: !field.notesOpen })}
                        aria-expanded={field.notesOpen}
                        aria-controls={`field-notes-${index}`}
                        className={`inline-flex shrink-0 items-center gap-1 text-[11px] ${
                          field.notes.trim()
                            ? 'text-ink-800/70 hover:text-ink-950'
                            : 'text-ink-800/40 hover:text-ink-800/70'
                        }`}
                      >
                        <StickyNote className="size-3" />
                        {field.notes.trim() ? 'Note' : 'Add note'}
                        {field.notesOpen ? (
                          <ChevronUp className="size-3" />
                        ) : (
                          <ChevronDown className="size-3" />
                        )}
                      </button>

                      {/* The note itself when folded away — truncated, with the whole of it on
                          hover, so a long one does not reintroduce the height just saved. */}
                      {!field.notesOpen && field.notes.trim() ? (
                        <span
                          className="min-w-0 flex-1 truncate text-[11px] text-ink-800/45"
                          title={field.notes}
                        >
                          {field.notes}
                        </span>
                      ) : null}
                    </div>

                    {field.notesOpen ? (
                      <input
                        id={`field-notes-${index}`}
                        value={field.notes}
                        onChange={(e) => setField(index, { notes: e.target.value })}
                        placeholder="Notes for the room filling this in (optional)"
                        aria-label={`Field ${index + 1} notes`}
                        maxLength={500}
                        className={`${inputClass} border-dashed py-1.5 text-xs`}
                      />
                    ) : null}
                  </div>
                ))}
              </div>

              {controls.fieldSetLocked ? null : (
                <button
                  type="button"
                  onClick={() => setFields((fs) => [...fs, emptyField()])}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-felt-700 hover:text-felt-900"
                >
                  <Plus className="size-3.5" /> Add field
                </button>
              )}

              {duplicateKey ? (
                <p className="mt-2 text-xs text-chip-500">
                  Two fields share a key. Keys identify the column in a room&apos;s content, so
                  they have to be distinct.
                </p>
              ) : null}
              {fields.length === 0 ? (
                <p className="mt-2 text-xs text-ink-800/50">A CSV template needs at least one field.</p>
              ) : null}
            </fieldset>
          ) : null}

          <ErrorNotice message={error} />
        </div>

        {/* The confirmation takes the whole footer rather than sitting beside "Save changes":
            the two are opposite intentions, and a destructive one should not share a row with
            the button next to it. */}
        <footer className="border-t border-ink-800/10 p-6">
          {editing && confirmDelete ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-ink-950">Delete this template?</p>
                <p className="mt-1 text-xs text-ink-800/60">
                  Every room&apos;s content for it is deleted with it, and that cannot be undone.
                  Type{' '}
                  <span className="font-mono text-ink-950">{template.name}</span> to confirm.
                </p>
              </div>

              <input
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder={template.name}
                aria-label="Type the template name to confirm deletion"
                autoFocus
                className={`${inputClass} font-mono`}
              />

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeDeleteConfirmation}
                  className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
                >
                  Keep template
                </button>
                <button
                  type="button"
                  onClick={() => deleteTemplate.mutate(template.id, { onSuccess: onClose })}
                  disabled={!deleteArmed || deleteTemplate.isPending}
                  className="flex-1 rounded-lg border border-chip-500/40 py-2 text-sm font-medium text-chip-500 hover:bg-chip-500/5 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  {deleteTemplate.isPending ? 'Deleting…' : 'Delete template'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              {/* Never on the players template (PI80): deleting it would drop every room's exclusions. */}
              {controls.deletable ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg border border-chip-500/40 px-4 py-2 text-sm text-chip-500 hover:bg-chip-500/5"
                >
                  Delete
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!valid || saving}
                className="flex-1 rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-50"
              >
                {saving
                  ? 'Saving…'
                  : editing
                    ? 'Save changes'
                    : intent.mode === 'clone'
                      ? 'Create copy'
                      : 'Create template'}
              </button>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
